import {
  Box,
  Typography,
  FormGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useEffect, useState, useRef } from "react";
import DialogContainer from "../../../components/dialog-container/dialog-container";
import { useFormik } from "formik";
import * as Yup from "yup";
import ErrorNotification from "../../../components/error-notification/error-notification";
import Loading from "../../../components/loading/loading";
import Notification from "../../../components/notification/notification";
import { useDispatch, useSelector } from "react-redux";
import ProjectSection from "../add-order/project-section/project-section";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CircularProgress from "@mui/material/CircularProgress";
import {
  createBulkOrdersRequest,
  downloadTemplateFileRequest,
} from "../../../utils/APICalls";
import {
  getClientAllUsersRequest,
  getClientsListRequest,
  getFormListByClientIdRequest,
  resetClientData,
} from "../../../redux/clients/slice";
import {
  getAdjustmentTypesRequest,
  getProjectsRequest,
} from "../../../redux/orders/slice";
import { DownloadOutlined } from "@mui/icons-material";

type props = {
  showDialog: boolean;
  closeDialog: any;
  addOrder: Function;
};

type FormValues = {
  clientId: string;
  project: string; // projectId
  requesterId: string;
  formId: string;
  file: File | null;
};

export default function BulkOrderDialog({
  showDialog,
  closeDialog,
  addOrder,
}: props) {
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "complete" | "failed"
  >("idle");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const uploadTimerRef = useRef<any>(null);
  const [bulkApiError, setBulkApiError] = useState<string>("");

  const { loading, clientsList, clientAllUsers, formsList } = useSelector(
    (state: any) => state.clients
  );

  const { projects, loading: projectsLoading } = useSelector(
    (state: any) => state.orders
  );

  const dispatch = useDispatch();

  const isCsvType = (file: File) => {
    const allowed = ["text/csv", "application/vnd.ms-excel"];
    return (
      allowed.includes(file.type) ||
      (file.name || "").toLowerCase().endsWith(".csv")
    );
  };

  const handleFileChange = (file: File | null) => {
    if (uploadTimerRef.current) {
      clearInterval(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }
    setBulkApiError("");
    setSelectedFile(file);
    setUploadProgress(0);
    setUploadStatus("idle");
    if (!file) {
      formik.setFieldValue("file", null);
      return;
    }
    if (!isCsvType(file)) {
      formik.setFieldError("file", "Only CSV files are allowed");
      formik.setFieldValue("file", file);
      setUploadStatus("failed");
      return;
    }
    if (file.size === 0) {
      formik.setFieldError("file", "Empty files are not allowed");
      formik.setFieldValue("file", file);
      setUploadStatus("failed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      formik.setFieldError("file", "File size exceeds 10MB limit");
      formik.setFieldValue("file", file);
      setUploadStatus("failed");
      return;
    }
    formik.setFieldError("file", undefined);
    formik.setFieldValue("file", file);
    setUploadStatus("uploading");
    let p = 0;
    uploadTimerRef.current = setInterval(() => {
      p += 5;
      setUploadProgress(p);
      if (p >= 100) {
        clearInterval(uploadTimerRef.current);
        uploadTimerRef.current = null;
        setUploadStatus("complete");
      }
    }, 150);
  };

  const handleFileDelete = () => {
    if (uploadTimerRef.current) {
      clearInterval(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }
    setBulkApiError("");
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStatus("idle");
    formik.setFieldValue("file", null);
    formik.setFieldError("file", undefined);
  };

  const validationSchema = Yup.object({
    clientId: Yup.string().required("This field can't be empty"),
    requesterId: Yup.string().required("This field can't be empty"),
    formId: Yup.string().required("This field can't be empty"),
    project: Yup.string().optional(),
    file: Yup.mixed()
      .required("CSV file is required")
      .test("fileType", "Only CSV files are allowed", (value) => {
        if (!value) return false;
        const f = value as File;
        const allowed = ["text/csv", "application/vnd.ms-excel"];
        return (
          allowed.includes(f.type) ||
          (f.name || "").toLowerCase().endsWith(".csv")
        );
      })
      .test("fileSize", "File size exceeds 10MB limit", (value) => {
        if (!value) return false;
        const f = value as File;
        return f.size > 0 && f.size <= 10 * 1024 * 1024;
      }),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      clientId: "",
      project: "",
      requesterId: "",
      formId: "",
      file: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setShowLoading(true);
        const formData = new FormData();
        formData.append("clientId", values.clientId);
        formData.append("requesterId", values.requesterId);
        formData.append("formId", values.formId);
        formData.append("projectId", values.project || "");
        if (values.file) formData.append("file", values.file);

        const response = await createBulkOrdersRequest(formData);

        if (
          response?.status === 200 ||
          response?.status === 201 ||
          response?.status === 204
        ) {
          setBulkApiError("");
          setSuccessMessage("Bulk order uploaded successfully");
          setShowSuccessNotification(true);
          addOrder();
          hideDialog();
        } else {
          const errMsg =
            (response && (response as any).data?.message) ||
            (response && (response as any).data?.detail) ||
            "Data validation was unsuccessful.";
          setBulkApiError(errMsg);
        }
      } catch (error) {
        setErrorMessage("An unexpected error occurred. Please try again.");
        setShowErrorNotification(true);
      } finally {
        setShowLoading(false);
      }
    },
  });

  useEffect(() => {
    dispatch(getClientsListRequest());
    dispatch(getAdjustmentTypesRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    const selectedClient = formik.values.clientId;
    if (selectedClient && selectedClient.toString().trim() !== "") {
      dispatch(getProjectsRequest({ clientId: selectedClient }));
      dispatch(getClientAllUsersRequest({ id: selectedClient }));
      dispatch(getFormListByClientIdRequest({ clientId: selectedClient }));
    }
  }, [formik.values.clientId]); // eslint-disable-line

  const hideDialog = () => {
    formik.resetForm();
    dispatch(resetClientData());
    setSelectedFile(null);
    closeDialog();
  };

  useEffect(() => {
    if (formik.values.clientId && formik.values.clientId !== "") {
      formik.setFieldValue("project", "");
      formik.setFieldValue("requesterId", "");
      formik.setFieldValue("formId", "");
    }
  }, [formik.values.clientId]); // eslint-disable-line

  const onDownloadTemplateClick = async () => {
    setShowLoading(true);
    try {
      const response = await downloadTemplateFileRequest("BulkOrderCreate");
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = "Bulk_Order_Template";
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <DialogContainer
      title="New Bulk Order"
      showDialog={showDialog}
      closeDialog={hideDialog}
      buttonText="SAVE"
      onSubmit={formik.handleSubmit}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <form onSubmit={formik.handleSubmit}>
          <Typography variant="h5">Order Details</Typography>
            <FormGroup sx={{ mt: 2}} >
              <FormControl fullWidth size="small">
                <InputLabel id="client-label">Client</InputLabel>
                <Select
                  labelId="client-label"
                  id="clientId"
                  name="clientId"
                  value={formik.values.clientId}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.clientId && Boolean(formik.errors.clientId)
                  }
                  label="Client"
                >
                  {clientsList?.map((client: any) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.clientName}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.clientId && formik.errors.clientId && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.clientId}
                  </Typography>
                )}
              </FormControl>
            </FormGroup>
            <FormGroup sx={{ mt: 2}} >
              <FormControl fullWidth size="small">
                <InputLabel id="requestor-label">Requestor</InputLabel>
                <Select
                  labelId="requestor-label"
                  id="requesterId"
                  name="requesterId"
                  value={formik.values.requesterId}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.requesterId &&
                    Boolean(formik.errors.requesterId)
                  }
                  label="Requestor"
                  disabled={!formik.values.clientId}
                >
                  {clientAllUsers?.map((user: any) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.fullName}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.requesterId && formik.errors.requesterId && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.requesterId}
                  </Typography>
                )}
              </FormControl>
            </FormGroup>
            <FormGroup sx={{ mt: 2, mb: 3}} >
              <FormControl fullWidth size="small">
                <InputLabel id="form-label">Form Type</InputLabel>
                <Select
                  labelId="form-label"
                  id="formId"
                  name="formId"
                  value={formik.values.formId}
                  onChange={formik.handleChange}
                  error={formik.touched.formId && Boolean(formik.errors.formId)}
                  label="Form Type"
                  disabled={!formik.values.clientId}
                >
                  {formsList?.map((form: any) => (
                    <MenuItem key={form?.id} value={form?.id}>
                      {form?.displayName}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.formId && formik.errors.formId && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.formId}
                  </Typography>
                )}
              </FormControl>
            </FormGroup>
          <ProjectSection
            formik={formik as any}
            projects={projects}
            setShowErrorNotification={setShowErrorNotification}
            setErrorMessage={setErrorMessage}
            setShowLoading={setShowLoading}
            setShowSuccessNotification={setShowSuccessNotification}
            setSuccessMessage={setSuccessMessage}
            disabled={!formik.values.clientId}
            includeOptionalLabel
          />
          <Typography variant="h5" sx={{ mt: 2}}>
            File
          </Typography>
          <Box sx={{ mt: 2}}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Before uploading your file, please download the template and
                copy your content into the appropriate fields, or make sure all
                fields in your file match those in the template. Fields that
                don’t apply can be left blank.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                sx={{ mb: 2, fontSize: "14px", fontWeight: 500 }}
                onClick={onDownloadTemplateClick}
              >
                <DownloadOutlined sx={{mr: 1}} /> DOWNLOAD TEMPLATE
              </Button>
            </Box>
            <Typography className="field-label-txt" sx={{mb: 2}}>Upload File</Typography>
            
            <Box >
              {uploadStatus === "idle" &&
                (!selectedFile || !formik.values.file) && (
                  <Box
                    sx={{
                      height: 80,
                      width: "100%",
                      border: formik.errors.file
                        ? "1px dashed #E53935"
                        : "1px dashed rgba(0, 0, 0, 0.12)",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const f = e.dataTransfer.files?.[0] || null;
                      if (f) handleFileChange(f);
                    }}
                  >
                    <UploadFileIcon sx={{ ml: 5 }} color="warning" />
                    <Box ml={2}>
                      <Box sx={{ display: "flex" }}>
                        <Typography
                          color="warning.main"
                          sx={{ textDecoration: "underline" }}
                        >
                          Click to upload
                        </Typography>
                        <Typography sx={{ ml: 1 }}>or drag and drop</Typography>
                      </Box>
                      <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                        CSV (max. 10MB)
                      </Typography>
                    </Box>
                    <input
                      style={{
                        display: "block",
                        height: 80,
                        width: "100%",
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        opacity: 0,
                        cursor: "pointer",
                      }}
                      type="file"
                      accept=".csv,text/csv,application/vnd.ms-excel"
                      onChange={(event) => {
                        const f =
                          (event.target as HTMLInputElement).files?.[0] || null;
                        if (f) handleFileChange(f);
                        (event.target as HTMLInputElement).value = "";
                      }}
                    />
                  </Box>
                )}
              {formik.errors.file && (
                <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error">
                  {formik.errors.file}
                </Typography>
              )}

              {uploadStatus === "uploading" && selectedFile && (
                <Box
                  sx={{
                    border: "1.5px solid #FB8C00",
                    background: "#FFF3E0",
                    borderRadius: 1,
                    p: 1.2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <AttachFileOutlinedIcon color="warning" />
                    <Box ml={1.5}>
                      <Typography>{selectedFile.name}</Typography>
                      <Typography sx={{ color: "rgba(0,0,0,0.6)" }}>
                        {`${(selectedFile.size / 1024).toFixed(0)} kb`} ·
                        Uploading
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <CircularProgress
                        variant="determinate"
                        value={uploadProgress}
                        size={32}
                        sx={{ color: "#FB8C00" }}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: "absolute",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          component="div"
                          color="warning.main"
                        >
                          {`${uploadProgress}%`}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      aria-label="delete"
                      onClick={handleFileDelete}
                      sx={{ color: "error.main" }}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {uploadStatus === "complete" && selectedFile && (
                <Box
                  sx={{
                    border: "1.5px solid #2E7D32",
                    background: "#F1F8E9",
                    borderRadius: 1,
                    p: 1.2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <AttachFileOutlinedIcon color="primary" />
                    <Box ml={1.5}>
                      <Typography>{selectedFile.name}</Typography>
                      <Typography sx={{ color: "rgba(0,0,0,0.6)" }}>
                        {`${(selectedFile.size / 1024).toFixed(0)} kb`} ·
                        Complete
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircleOutlineIcon color="success" />
                    <IconButton
                      aria-label="delete"
                      onClick={handleFileDelete}
                      sx={{ color: "error.main" }}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {uploadStatus === "failed" && selectedFile && (
                <Box
                  sx={{
                    border: "1.5px solid #E53935",
                    background: "#FFEBEE",
                    borderRadius: 1,
                    p: 1.2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <AttachFileOutlinedIcon color="error" />
                    <Box ml={1.5}>
                      <Typography>Upload failed.</Typography>
                      <Typography sx={{ color: "error.main" }}>
                        {formik.errors.file || "File error"} · Failed
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorOutlineIcon color="error" />
                    <IconButton
                      aria-label="delete"
                      onClick={handleFileDelete}
                      sx={{ color: "error.main" }}
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          
          {bulkApiError && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "error.main",
                color: "#fff",
                borderRadius: 1,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <ErrorOutlineIcon sx={{ color: "#fff", flexShrink: 0 }} />
              <Typography
                sx={{
                  fontWeight: 500,
                  flex: 1,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {bulkApiError}
              </Typography>
            </Box>
          )}

        </form>
      </LocalizationProvider>
      {(loading || showLoading || projectsLoading) && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      <Notification
        show={showSuccessNotification}
        message={successMessage}
        onClose={() => setShowSuccessNotification(false)}
      />
    </DialogContainer>
  );
}
